import React from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// redux
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/actions/behaviouralForm";

// CreateBehaviouralDto: {userId, breakfastIntakeTime, lunchIntakeTime,
// dinnerIntakeTime, otherIntakeTime, sleepTime, sleepDuration, sleeper,
// exercise, walk, miles, medication, hobbies} — all plain free-text, no
// dropdown-option lookups on this backend (unlike the old one).
const emptyForm = {
  breakfastIntakeTime: "",
  lunchIntakeTime: "",
  dinnerIntakeTime: "",
  otherIntakeTime: "",
  sleepTime: "",
  sleepDuration: "",
  sleeper: "",
  exercise: "",
  walk: "",
  miles: "",
  medication: "",
  hobbies: "",
};

const FIELD_GROUPS = [
  {
    label: "Food Intake Time",
    fields: [
      { name: "breakfastIntakeTime", label: "Breakfast" },
      { name: "lunchIntakeTime", label: "Lunch" },
      { name: "dinnerIntakeTime", label: "Dinner" },
      { name: "otherIntakeTime", label: "Other" },
    ],
  },
  {
    label: "Sleep",
    fields: [
      { name: "sleepTime", label: "Sleep Time" },
      { name: "sleepDuration", label: "Sleep Duration" },
      { name: "sleeper", label: "Sleeper" },
    ],
  },
  {
    label: "Activity",
    fields: [
      { name: "exercise", label: "Exercise" },
      { name: "walk", label: "Walk" },
      { name: "miles", label: "Miles" },
    ],
  },
  {
    label: "Other",
    fields: [
      { name: "medication", label: "Medication" },
      { name: "hobbies", label: "Hobbies" },
    ],
  },
];

const BehaviouralJournalForm = () => {
  const dispatch = useDispatch();
  const behaviouralForm = useSelector((state) => state.behaviouralForm);

  const [form, setForm] = React.useState(emptyForm);

  React.useEffect(() => {
    dispatch(actions.getBehaviouralForms());
  }, [dispatch]);

  React.useEffect(() => {
    if (behaviouralForm.saved) {
      setForm(emptyForm);
      dispatch(actions.getBehaviouralForms());
    }
  }, [behaviouralForm.saved, dispatch]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    dispatch(actions.createBehaviouralForm(form));
  };

  return (
    <Card sx={{ width: "100%" }}>
      <MDBox display="flex">
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          variant="gradient"
          bgColor="primary"
          color="white"
          shadow="md"
          borderRadius="xl"
          ml={3}
          mt={-2}
        >
          <Icon fontSize="medium" color="inherit">
            edit_note
          </Icon>
        </MDBox>
        <MDBox>
          <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
            Daily Journal
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox p={3}>
        <MDTypography variant="button" color="text" sx={{ display: "block", mb: 2 }}>
          A more detailed daily log — meals, sleep, activity and hobbies. This is separate
          from the quick score above; submitting adds a new dated entry each time.
        </MDTypography>

        {FIELD_GROUPS.map((group) => (
          <MDBox key={group.label} mt={2}>
            <MDTypography variant="subtitle2" fontWeight="bold">
              {group.label}
            </MDTypography>
            <MDBox mt={1}>
              <Grid container spacing={3}>
                {group.fields.map(({ name, label }) => (
                  <Grid item xs={12} sm={6} md={3} key={name}>
                    <MDInput
                      type="text"
                      label={label}
                      name={name}
                      value={form[name]}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </MDBox>
        ))}

        <MDBox mt={4} mb={1}>
          <MDButton
            variant="gradient"
            color="primary"
            onClick={!behaviouralForm.saving ? handleSubmit : undefined}
          >
            {behaviouralForm.saving ? "Saving..." : "Add Journal Entry"}
          </MDButton>
        </MDBox>

        {behaviouralForm.forms?.length > 0 && (
          <MDBox mt={4}>
            <MDTypography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
              Recent Entries
            </MDTypography>
            {behaviouralForm.forms.map((entry, idx) => (
              <MDBox
                key={idx}
                p={2}
                mb={1}
                borderRadius="lg"
                sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}
              >
                <Grid container spacing={1}>
                  {FIELD_GROUPS.flatMap((g) => g.fields).map(({ name, label }) =>
                    entry[name] ? (
                      <Grid item xs={6} sm={3} key={name}>
                        <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                          {label}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="medium">
                          {entry[name]}
                        </MDTypography>
                      </Grid>
                    ) : null
                  )}
                </Grid>
              </MDBox>
            ))}
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

export default BehaviouralJournalForm;
